import {
  ExclamationCircleFilled,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import type { FormInstance } from "antd";
import { Button, Form, Spin, Typography } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import * as commonColumns from "../../common/columns.tsx";
import { Card } from "../../common/components/Card.tsx";
import { CountryFlag } from "../../common/components/CountryFlag.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
  type PaginationAndFilter,
  TableWrapper,
  type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { sanitizedFilter } from "../../common/functions.ts";
import { locationActions } from "../../features/location/locationSlice.ts";
import ConnectedAppLayout from "../AppLayout.tsx";
import { locationFilter } from "../common/filter.ts";
import { routeNames } from "../common/routes.ts";
import type { Location, PaginatedLocationList } from "../generated-axios-client/index.ts";
import type { RootState } from "../store.ts";

const onFilter =
  (dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
    form
      .validateFields()
      .then((values) => {
        dispatch(
          routerActions.navigateTo(
            currentRoute.name,
            sanitizedFilter({ ...currentRoute.params, ...values, page: 1 })
          )
        );
      })
      .catch((error) => {
        console.log("Form validation error:", error);
      });
  };

const onCancel =
  (dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
    dispatch(
      routerActions.navigateTo(
        currentRoute.name,
        sanitizedFilter({ ...currentRoute.params, ...locationFilter, page: 1 })
      )
    );
    form.resetFields();
  };

const getColumns: TableWrapperProps<PaginatedLocationList, AnyObject>["getColumns"] = (
  intl,
  currentFilter
) => [
  commonColumns.simpleColumn("row_number", "table_column_ticket_number", intl),
  commonColumns.simpleColumn(
    "name",
    "location_table_column_name",
    intl,
    currentFilter
  ),
  {
    dataIndex: "country",
    key: "country",
    title: intl.formatMessage({ id: "location_table_column_country" }),
    render: (countryCode: string) => <CountryFlag countryCode={countryCode} />,
    defaultSortOrder: commonColumns.defaultSort("country", currentFilter),
    sorter: !!currentFilter,
  },
  commonColumns.simpleColumn(
    "description",
    "location_table_column_description",
    intl,
    currentFilter
  ),
  commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<PaginatedLocationList, AnyObject>["getRows"] = (
  intl,
  dispatch,
  data,
  modalApi
) => {
  return data.results.map((location: Location, index: number) => {
    const showDeleteConfirm = () => {
      modalApi?.confirm({
        title: intl.formatMessage({ id: "location_delete_confirmation" }),
        icon: <ExclamationCircleFilled />,
        okText: intl.formatMessage({ id: "confirm" }),
        okType: "danger",
        cancelText: intl.formatMessage({ id: "cancel" }),
        onOk() {
          if (location.id) {
            dispatch(locationActions.deleteLocation(location.id));
          }
        },
        onCancel() {},
      });
    };

    const onView = () => {
      dispatch(routerActions.navigateTo(routeNames.LOCATION_VIEW, { id: location.id }));
    };

    const onEdit = () => {
      dispatch(routerActions.navigateTo(routeNames.LOCATION_EDIT, { id: location.id }));
    };

    return {
      key: location.id,
      row_number: index + 1,
      name: location.id ? (
        <Typography.Link onClick={onView}>{location.name}</Typography.Link>
      ) : (
        location.name
      ),
      country: location.country,
      description: location.description,
      actions: commonColumns.actionsValue(
        undefined,
        undefined,
        undefined,
        undefined,
        onView,
        onEdit,
        showDeleteConfirm,
        undefined,
        "Site"
      ),
    };
  });
};

type LocationsPageProps = ReturnType<typeof withLocations> & DispatchProp;

export const LocationsPage = (props: LocationsPageProps) => {
  const { results, dispatch, currentRoute, intl, modalApi } = props;

  const [form] = Form.useForm();

  if (!currentRoute) {
    return <Spin indicator={<LoadingOutlined spin />} size="large" />;
  }

  const pagination = results.pagination;

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "location_table_header_name" })}
        subtitle={intl.formatMessage(
          { id: "location_table_header_number_of_items" },
          { no: pagination.total_items }
        )}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => dispatch(routerActions.navigateTo(routeNames.LOCATION_CREATE))}
          >
            {intl.formatMessage({ id: "location_create_button" })}
          </Button>
        }
      >
        <Card
          title={
            <ListActionBar
              intl={intl}
              form={form}
              onFilter={onFilter(dispatch, form, currentRoute)}
              onCancel={onCancel(dispatch, form, currentRoute)}
              filterTemplate={locationFilter}
              currentFilter={currentRoute.params}
              searchBarPlaceholderKey={intl.formatMessage({
                id: "location_table_header_search_placeholder",
              })}
              filterDisabled
            />
          }
        >
          <TableWrapper
            data={results}
            getRows={getTableRows}
            getColumns={getColumns}
            intl={intl}
            modalApi={modalApi}
            route={currentRoute}
            applyFilterAction={(filterParams) =>
              routerActions.navigateTo(routeNames.LOCATION_LIST, filterParams)
            }
            pagination={pagination as PaginationAndFilter}
          />
        </Card>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withLocations = (state: RootState) => ({
  results: state.location.list,
  currentRoute: state.router.route,
  intl: state.intl.datetimeIntl,
  modalApi: state.appFeedback.modal,
});

export const ConnectedLocationsPage = connect(withLocations)(LocationsPage);
export default ConnectedLocationsPage;
